import { QueryClient } from "@tanstack/react-query";
import { shouldRetryQuery } from "./retry-policy.js";
/**
 * How long a response is served without a background refetch. Long enough that navigating away
 * and back is not a network round trip, short enough that a figure on screen is not minutes old.
 */
export const DEFAULT_STALE_TIME_MS = 30_000;
/**
 * How long an unobserved response is kept before eviction. It must outlive `staleTime`, or the
 * cache is gone before the stale window it defines has any cached render to serve.
 */
export const DEFAULT_GC_TIME_MS = 5 * 60_000;
/**
 * The application's query client.
 *
 * A factory rather than a module-level instance, and that is the load-bearing part. FE-09 requires
 * an organization or site change to clear every query and mutation scoped to the previous
 * selection; replacing the client is the coarsest and most complete way to do that, and a shared
 * singleton removes the option. It also keeps one test's cache out of the next.
 *
 * Query keys are not defined here. FE-09 gives each capability explicit ownership of its own, so
 * a central key registry would be the wrong shape - M7 routes own theirs.
 */
export const createQueryClient = () => new QueryClient({
    defaultOptions: {
        mutations: {
            /**
             * Never. A mutation is a write, and nothing behind the `Idempotency-Key` header
             * deduplicates yet (`AGENTS.md`: the header is validated and otherwise inert). A retried
             * write whose first attempt already landed is a duplicate, and on a payment surface that
             * is the defect the whole idempotency seam exists to prevent. This becomes a decision to
             * revisit when M0-04's seam has a real idempotency capability behind it, not before.
             */
            retry: false,
        },
        queries: {
            gcTime: DEFAULT_GC_TIME_MS,
            /**
             * Off. With brand and entitlement resolved server-side, a refocus refetch storm asks the
             * API to re-answer every mounted query because someone switched to their mail client.
             * A capability that genuinely needs focus freshness turns it on for its own query.
             */
            refetchOnWindowFocus: false,
            retry: shouldRetryQuery,
            staleTime: DEFAULT_STALE_TIME_MS,
        },
    },
});
//# sourceMappingURL=query-client.js.map